import React from 'react';
import { MapPin, Clock, Calendar, ArrowLeft, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { EventDetailViewProps } from './eventdetailpage.props';

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const months = ['јануари', 'февруари', 'март', 'април', 'мај', 'јуни', 'јули', 'август', 'септември', 'октомври', 'ноември', 'декември'];
  return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
};

const EventDetailView: React.FC<EventDetailViewProps> = ({ event, loading, navigate }) => {
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-10 w-24 mb-6" />
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <Skeleton className="h-96 w-full" />
            <div className="p-8">
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <Skeleton className="h-48 w-full" />
                <Skeleton className="h-48 w-full" />
              </div>
              <Skeleton className="h-32 w-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Настанот не е пронајден</h2>
          <Button onClick={() => navigate('/')}>Врати се на почетна</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Назад
        </Button>

        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="relative h-96">
            <img src={event.image} alt={event.title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
              <h1 className="text-4xl font-bold mb-2">{event.title}</h1>
              {event.subtitle && <p className="text-lg opacity-90">{event.subtitle}</p>}
            </div>
          </div>

          <div className="p-8">
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-4 text-gray-900">Детали за настанот</h3>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <MapPin className="w-5 h-5 mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-gray-600">Локација</p>
                        <p className="font-medium text-gray-900">{event.location}</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <Calendar className="w-5 h-5 mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-gray-600">Датум</p>
                        <p className="font-medium text-gray-900">{formatDate(event.date)}</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <Clock className="w-5 h-5 mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-gray-600">Време</p>
                        <p className="font-medium text-gray-900">{event.time}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-4 text-gray-900">Организатор</h3>
                  <div className="flex items-center space-x-3 mb-4">
                    <ExternalLink className="w-5 h-5 text-blue-600" />
                    <p className="font-medium text-gray-900">{event.organizer}</p>
                  </div>
                  {event.status === 'upcoming' && (
                    <Button
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      onClick={() => navigate(`/events/${event.id}/register`)}
                    >
                      Пријави се за настанот
                    </Button>
                  )}
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold text-lg mb-4 text-gray-900">За настанот</h3>
                <div className="prose max-w-none text-gray-700">
                  <p>
                    Овој настан е организиран од {event.organizer} и представува одлична прилика за сите любители на трчање и спорт.
                  </p>
                  <p className="mt-4">
                    Придружете ни се на {formatDate(event.date)} во {event.time} часот во {event.location} за едно незаборавно доживување!
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventDetailView;
